import React from "react";
import { ChevronDown, ChevronUp, Copy, GripVertical, Trash2 } from "lucide-react";

import { Badge } from "../../../../components/badge";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { Input } from "../../../../components/input";
import { Label } from "../../../../components/label";
import { Switch } from "../../../../components/switch";
import { FormErrorText } from "../../../../components/form/FormErrorText";
import QuestionTypeIcon from "../icons/QuestionTypeIcon";
import type { SurveyQuestionType } from "../../data/questionTypes";
import type { SurveyQuestion } from "../../types/survey.types";

interface QuestionEditorProps {
  question: SurveyQuestion;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onUpdateQuestion: (updates: Partial<SurveyQuestion>) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  questionErrorMessage?: string;
  onQuestionRefChange?: (element: HTMLDivElement | null) => void;
  onDragHandleRefChange?: (element: Element | null) => void;
}

// Persisted API keys remain unchanged; these labels only describe respondent selection behavior.
const QUESTION_TYPE_LABELS: Partial<Record<SurveyQuestionType, string>> = {
  multiple_choice: "Single Choice",
  multiple_answer: "Multiple Choice",
};

const formatQuestionType = (type: SurveyQuestionType): string =>
  QUESTION_TYPE_LABELS[type] ??
  type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export function QuestionEditor({
  question,
  index,
  isSelected,
  onSelect,
  onDuplicate,
  onDelete,
  onUpdateQuestion,
  onMoveUp,
  onMoveDown,
  questionErrorMessage,
  onQuestionRefChange,
  onDragHandleRefChange,
}: QuestionEditorProps) {
  const questionId = question.client_id ?? `question-${question.position}`;

  return (
    <div ref={onQuestionRefChange}>
      <Card
        className={`mb-4 transition-all duration-200 ${isSelected ? "ring-2 ring-blue-500 shadow-lg" : "hover:shadow-md"}`}
        onClick={onSelect}
      >
        <div className="p-4">
          <div className="mb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <button
                  ref={onDragHandleRefChange}
                  type="button"
                  className="rounded p-1 text-gray-400 cursor-grab active:cursor-grabbing hover:bg-gray-100 dark:hover:bg-gray-700"
                  aria-label="Kéo để sắp xếp"
                  onClick={(e) => e.stopPropagation()}
                >
                  <GripVertical className="h-4 w-4" />
                </button>
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "#F2FCFA" }}
                >
                  <QuestionTypeIcon type={question.type} />
                </div>
                <div className="flex flex-col">
                  <Badge
                    variant="outline"
                    className="text-xs w-fit"
                  >
                    {formatQuestionType(question.type)}
                  </Badge>
                  <span className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Question {question.position || index + 1}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  title="Move question up"
                  aria-label="Move question up"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveUp?.();
                  }}
                  disabled={!onMoveUp}
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  title="Move question down"
                  aria-label="Move question down"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveDown?.();
                  }}
                  disabled={!onMoveDown}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  title="Nhân bản câu hỏi"
                  aria-label="Nhân bản câu hỏi"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicate();
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  title="Xóa câu hỏi"
                  aria-label="Xóa câu hỏi"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor={questionId}>Question</Label>
                <Input
                  id={questionId}
                  value={question.data.question}
                  onChange={(e) =>
                    onUpdateQuestion({
                      data: { ...question.data, question: e.target.value },
                    })
                  }
                  placeholder="Enter your question"
                  className={`mt-1 ${questionErrorMessage ? "border-red-500 dark:border-red-500" : ""}`}
                />
                <FormErrorText message={questionErrorMessage} />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id={`required-${questionId}`}
                  checked={question.is_required}
                  onCheckedChange={(checked: boolean) =>
                    onUpdateQuestion({ is_required: checked })
                  }
                />
                <Label htmlFor={`required-${questionId}`}>Required</Label>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default QuestionEditor;
